// js/_shared/_local-time.js
/* =============================================================================
   SHARED LOCAL TIME CONVERTER
   Path: /js/_shared/_local-time.js

   Purpose: Converts UTC timestamps rendered as data-utc attributes to the
            user's local timezone.  Standardises all date/time display formats
            across the application.

   Dependencies: none (uses AppUtils namespace for convention only)

   Namespace: window.AppUtils.LocalTime

   Usage (automatic):
     Server renders:
       <span class="local-time"
             data-utc="@Model.SomeDateUtc.ToString("o")"
             data-format="datetime">
         @Model.SomeDateUtc.ToString("MMM d, yyyy h:mm tt") UTC
       </span>
     Module auto-converts on DOMContentLoaded.

   Usage (manual - AJAX-loaded content):
     AppUtils.LocalTime.convert(newContainer);

   data-format values:
     "datetime" (default) - Feb 3, 2026 3:45 PM
     "date"               - Feb 3, 2026
     "time"               - 3:45 PM
     "short"              - Feb 3, 3:45 PM
     "chart"              - Feb 3
   ========================================================================== */

window.AppUtils = window.AppUtils || {};

window.AppUtils.LocalTime = (function () {
    'use strict';

    // ── Format definitions ───────────────────────────────────────────
    var FORMATS = {
        datetime: { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
        date:     { year: 'numeric', month: 'short', day: 'numeric' },
        time:     { hour: 'numeric', minute: '2-digit', hour12: true },
        short:    { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
        chart:    { month: 'short', day: 'numeric' }
    };

    var _tzName = null;

    // ── Private helpers ──────────────────────────────────────────────

    function getTzName() {
        if (!_tzName) {
            try { _tzName = Intl.DateTimeFormat().resolvedOptions().timeZone; }
            catch (e) { _tzName = 'Unknown'; }
        }
        return _tzName;
    }

    function formatDate(date, formatKey) {
        var opts = FORMATS[formatKey] || FORMATS.datetime;
        return date.toLocaleString(undefined, opts);
    }

    function buildTooltip(date) {
        var utcStr = date.toISOString().replace('T', ' ').substring(0, 16);
        return 'UTC: ' + utcStr + ' | Local (' + getTzName() + ')';
    }

    // ── Core conversion ──────────────────────────────────────────────

    /**
     * Convert a single element with data-utc attribute.
     * @param {Element} el
     */
    function convertElement(el) {
        var utcString = el.getAttribute('data-utc');
        if (!utcString) return;

        var d = new Date(utcString);
        if (isNaN(d.getTime())) return;

        var fmt = el.getAttribute('data-format') || 'datetime';
        el.textContent = formatDate(d, fmt);
        el.title = buildTooltip(d);
        el.setAttribute('data-converted', 'true');
    }

    // ── Public API ───────────────────────────────────────────────────

    /**
     * Convert all .local-time[data-utc] elements within a root.
     * Safe to call multiple times (skips already-converted elements).
     * @param {Element} [root=document] - Container to search within
     */
    function convert(root) {
        var container = root || document;
        var els = container.querySelectorAll('.local-time[data-utc]:not([data-converted])');
        for (var i = 0; i < els.length; i++) {
            convertElement(els[i]);
        }
    }

    /**
     * Format a UTC ISO string to the specified format.
     * For use in JS code that builds HTML dynamically.
     * @param {string} utcString - ISO 8601 date string
     * @param {string} [formatKey='datetime'] - One of: datetime, date, time, short, chart
     * @returns {string} Formatted local date string
     */
    function format(utcString, formatKey) {
        var d = new Date(utcString);
        if (isNaN(d.getTime())) return utcString || '';
        return formatDate(d, formatKey || 'datetime');
    }

    /**
     * Get the user's IANA timezone name.
     * @returns {string} e.g. "America/Chicago"
     */
    function timezone() {
        return getTzName();
    }

    // ── Auto-init ────────────────────────────────────────────────────

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { convert(); });
    } else {
        convert();
    }

    return {
        convert: convert,
        format: format,
        timezone: timezone
    };

})();
